const SWIPE_MIN_DISTANCE = 40;

class ThroughTimeSection extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.tabButtons = [...this.querySelectorAll('[data-through-time-tab]')];
    this.panels = [...this.querySelectorAll('through-time-panel')];
    this.currentTabIndex = Number(
      this.panels.find((panel) => panel.classList.contains('is-active'))?.dataset.tabIndex ?? 1
    );

    this.bindTabEvents();
    this.panels.forEach((panel) => this.initPanel(panel));

    if (this.tabButtons.length > 1) {
      this.activateTab(this.currentTabIndex || 1);
    } else if (this.panels[0]) {
      this.showPanel(this.panels[0]);
    }

    this.mq = window.matchMedia('(max-width: 767px)');
    this.onLayout = () => this.layoutActiveWheels();
    this.resizeObserver = new ResizeObserver(this.onLayout);
    this.resizeObserver.observe(this);
    if (typeof this.mq.addEventListener === 'function') {
      this.mq.addEventListener('change', this.onLayout);
    } else {
      this.mq.addListener(this.onLayout);
    }
    this.layoutActiveWheels();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    if (!this.mq) return;
    if (typeof this.mq.removeEventListener === 'function') {
      this.mq.removeEventListener('change', this.onLayout);
    } else {
      this.mq.removeListener(this.onLayout);
    }
  }

  showPanel(panel) {
    this.panels.forEach((item) => {
      const isActive = item === panel;
      item.classList.toggle('is-active', isActive);
      item.hidden = !isActive;
      if (isActive) {
        item.goToSlide?.(0);
        this.scheduleLayout(item);
      }
    });
  }

  bindTabEvents() {
    if (this.tabButtons.length <= 1) return;

    this.tabButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const index = Number(button.dataset.throughTimeTab);
        if (!index || index === this.currentTabIndex) return;
        this.activateTab(index);
      });
    });
  }

  activateTab(index) {
    this.currentTabIndex = index;

    this.tabButtons.forEach((button) => {
      const isActive = Number(button.dataset.throughTimeTab) === index;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    const activePanel = this.panels.find((panel) => Number(panel.dataset.tabIndex) === index);
    if (activePanel) this.showPanel(activePanel);
  }

  initPanel(panel) {
    const slides = [...panel.querySelectorAll('[data-through-time-slide]')];
    if (!slides.length) return;

    const wheelMarkers = [...panel.querySelectorAll('[data-through-time-wheel-marker]')];
    const prevButtons = [...panel.querySelectorAll('[data-through-time-prev]')];
    const nextButtons = [...panel.querySelectorAll('[data-through-time-next]')];
    const shopNow = panel.querySelector('[data-through-time-shop-now]');
    const stage = panel.querySelector('[data-through-time-stage]');

    const controller = {
      currentIndex: -1,
      touchStartX: 0,
      touchStartY: 0,
    };

    controller.goTo = (index) => {
      if (index < 0 || index >= slides.length) return;
      controller.currentIndex = index;

      slides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-active', slideIndex === index);
      });

      wheelMarkers.forEach((marker, markerIndex) => {
        const isActive = markerIndex === index;
        marker.classList.toggle('is-active', isActive);
        marker.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      // Set on the panel so every wheel container inherits it, not just the markers.
      panel.style.setProperty('--tt-active-index', `${index}`);

      controller.updatePagination();
      controller.updateShopNow();
    };

    controller.updatePagination = () => {
      const atStart = controller.currentIndex <= 0;
      const atEnd = controller.currentIndex >= slides.length - 1;

      prevButtons.forEach((button) => {
        button.disabled = atStart;
      });
      nextButtons.forEach((button) => {
        button.disabled = atEnd;
      });
    };

    controller.updateShopNow = () => {
      if (!shopNow) return;

      const buttonUrl = slides[controller.currentIndex]?.dataset.buttonUrl || '';

      shopNow.href = buttonUrl || '#';
      shopNow.classList.toggle('is-empty', !buttonUrl);
      shopNow.setAttribute('data-sensors-click-property-rank', String(controller.currentIndex + 1));
      shopNow.setAttribute('data-sensors-click-property-href_url', buttonUrl);
    };

    wheelMarkers.forEach((marker) => {
      marker.addEventListener('click', () => {
        const slideIndex = Number(marker.dataset.throughTimeWheelMarker);
        if (!Number.isNaN(slideIndex)) controller.goTo(slideIndex);
      });
    });

    prevButtons.forEach((button) => {
      button.addEventListener('click', () => controller.goTo(controller.currentIndex - 1));
    });

    nextButtons.forEach((button) => {
      button.addEventListener('click', () => controller.goTo(controller.currentIndex + 1));
    });

    stage?.addEventListener(
      'touchstart',
      (event) => {
        const touch = event.changedTouches[0];
        controller.touchStartX = touch.clientX;
        controller.touchStartY = touch.clientY;
      },
      { passive: true }
    );

    stage?.addEventListener(
      'touchend',
      (event) => {
        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - controller.touchStartX;
        const deltaY = touch.clientY - controller.touchStartY;
        if (Math.abs(deltaX) < SWIPE_MIN_DISTANCE || Math.abs(deltaX) < Math.abs(deltaY)) return;

        controller.goTo(controller.currentIndex + (deltaX < 0 ? 1 : -1));
      },
      { passive: true }
    );

    panel.goToSlide = controller.goTo;
    controller.goTo(0);
    this.scheduleLayout(panel);
  }

  scheduleLayout(panel) {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => this.layoutWheels(panel || this.getActivePanel()));
    });
  }

  layoutActiveWheels() {
    this.layoutWheels(this.getActivePanel());
  }

  getActivePanel() {
    return this.panels.find((panel) => panel.classList.contains('is-active')) || this.panels[0];
  }

  layoutWheels(panel) {
    if (!panel) return;

    const isMobile = this.mq ? this.mq.matches : window.matchMedia('(max-width: 767px)').matches;
    const top = panel.querySelector('.through-time__wheel-top');
    if (isMobile && top) {
      const halfW = top.clientWidth / 2;
      const sagitta = Math.max(top.clientHeight - 2, 1);
      if (halfW > 0) {
        const radius = (halfW * halfW) / (2 * sagitta) + sagitta / 2;
        top.style.setProperty('--tt-r', `${radius}px`);
        top.style.setProperty('--tt-diameter', `${radius * 2}px`);
      }
    }

    const markerTrack = panel.querySelector(
      '.through-time__wheel-container--markers .through-time__wheel-track'
    );
    const markers = [...panel.querySelectorAll('[data-through-time-wheel-marker]')];
    if (!markerTrack || !markers.length) return;

    let radius = markerTrack.offsetWidth / 2;
    if (!radius) {
      const host = panel.closest('.through-time') || panel;
      const hostW = host.clientWidth || window.innerWidth;
      radius = isMobile ? (313 / 375) * hostW : markerTrack.offsetWidth / 2;
    }
    if (!radius) return;

    const styles = window.getComputedStyle(panel.closest('.through-time') || panel);
    const start = Number.parseFloat(styles.getPropertyValue('--tt-start-angle')) || (isMobile ? -90 : 0);
    const step = Number.parseFloat(styles.getPropertyValue('--tt-step-angle')) || (isMobile ? 25 : 19.3);

    markers.forEach((marker) => {
      const index = Number(marker.dataset.throughTimeWheelMarker);
      if (Number.isNaN(index)) return;
      const deg = start + step * index;
      const rad = (deg * Math.PI) / 180;
      marker.style.setProperty('--tt-marker-angle-deg', `${deg}deg`);
      marker.style.setProperty('--tt-marker-x', `${radius + radius * Math.cos(rad)}px`);
      marker.style.setProperty('--tt-marker-y', `${radius + radius * Math.sin(rad)}px`);
    });
  }
}

class ThroughTimePanel extends HTMLElement {
  connectedCallback() {
    this.hidden = !this.classList.contains('is-active');
  }
}

if (!customElements.get('through-time-section')) {
  customElements.define('through-time-section', ThroughTimeSection);
}

if (!customElements.get('through-time-panel')) {
  customElements.define('through-time-panel', ThroughTimePanel);
}
